// Pure parser for the QuotesPanel's bulk-paste box (QUOTES_CONTRACT.md §5). No
// React, no I/O — just text → Quote[], so it doubles as the seed-file generator
// and a unit-friendly fixture for the same paste format Mom uses.
//
// Format: quotes are separated by BLANK lines (one or more empty/whitespace-only
// lines). Within a block, the FIRST line is the `attribution` (who said it) and the
// remaining lines, joined with "\n", are the `text`. A single-line block has no
// attribution — the whole line is the `text`. Everything is trimmed; a block whose
// `text` is empty after trim is dropped.

import type { Quote } from "./types";

/**
 * Parse a pasted blob of quotes into `Quote[]`. Blocks are split on blank lines;
 * each block's first line becomes `attribution` and the rest (joined with "\n")
 * becomes `text`; a single-line block is `{ text }` with no attribution. Trims all
 * fields and drops blocks with empty `text`. PURE.
 */
export function parseQuotesBlob(blob: string): Quote[] {
  const quotes: Quote[] = [];
  // Normalize newlines, then split into blocks on runs of blank (whitespace-only)
  // lines so a paste with \r\n or padded blank separators parses the same way.
  const blocks = blob.replace(/\r\n?/g, "\n").split(/\n[ \t]*\n+/);

  for (const block of blocks) {
    // Trim each line, then drop leading/trailing blank lines inside the block so a
    // block can't carry an empty first ("attribution") line from stray whitespace.
    const lines = block.split("\n").map((l) => l.trim());
    while (lines.length && lines[0] === "") lines.shift();
    while (lines.length && lines[lines.length - 1] === "") lines.pop();
    if (lines.length === 0) continue;

    if (lines.length === 1) {
      // Single line → it's the text; no attribution.
      const text = lines[0];
      if (text) quotes.push({ text });
      continue;
    }

    // First line = attribution, the rest (joined) = the quote body.
    const attribution = lines[0];
    const text = lines.slice(1).join("\n").trim();
    if (!text) continue; // nothing said — drop it
    quotes.push(attribution ? { attribution, text } : { text });
  }

  return quotes;
}
